import { config } from '@/auth/webconfig';
import { polarClient } from '@polar-sh/better-auth';
import { useQuery } from '@tanstack/react-query';
import { organizationClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { createContext, useContext } from 'react';

const authClient = createAuthClient({
  baseURL: config.apiUrl,
  plugins: [organizationClient(), polarClient()],
  fetchOptions: {
    credentials: 'include',
  },
});
type ContextType = typeof authClient;

interface AuthContextValue {
  signIn: ContextType['signIn'];
  signOut: ContextType['signOut'];
  signUp: ContextType['signUp'];
  getSession: ContextType['getSession'];
  useSession: ContextType['useSession'];
  create: ContextType['organization']['create'];
  setActive: ContextType['organization']['setActive'];
  useListOrganizations: ContextType['useListOrganizations'];
  useActiveOrganization: ContextType['useActiveOrganization'];
  useActiveMember: ContextType['useActiveMember'];
  update: ContextType['organization']['update'];
  deleteOrg: ContextType['organization']['delete'];
  getFullOrganization: ContextType['organization']['getFullOrganization'];
  // Invitation methods
  inviteMember: ContextType['organization']['inviteMember'];
  acceptInvitation: ContextType['organization']['acceptInvitation'];
  rejectInvitation: ContextType['organization']['rejectInvitation'];
  cancelInvitation: ContextType['organization']['cancelInvitation'];
  listInvitations: ContextType['organization']['listInvitations'];
  checkout: ContextType['checkout'];
  portal: ContextType['customer']['portal'];

  customer: ContextType['customer'];
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    signIn,
    signOut,
    signUp,
    getSession,
    useSession,
    organization: {
      create,
      setActive,
      update,
      delete: deleteOrg,
      getFullOrganization,
      acceptInvitation,
      rejectInvitation,
      cancelInvitation,
      listInvitations,
      inviteMember,
    },
    checkout,
    useListOrganizations,
    useActiveOrganization,
    useActiveMember,
    customer,
  } = authClient;

  return (
    <AuthContext.Provider
      value={{
        signIn,
        signOut,
        signUp,
        getSession,
        useSession,
        create,
        setActive,
        useListOrganizations,
        useActiveOrganization,
        useActiveMember,
        update,
        deleteOrg,
        getFullOrganization,
        inviteMember,
        acceptInvitation,
        rejectInvitation,
        cancelInvitation,
        listInvitations,
        checkout,
        portal: customer.portal,
        customer,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useCurrentSubscriptions() {
  const { useSession, customer } = useAuth();
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['customer.subscriptions'],
    queryFn: async () => {
      return await customer.subscriptions.list({
        query: {
          active: true,
        },
      });
    },
    enabled: !!session,
  });
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
